import { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, IconButton, InputAdornment, InputLabel, MenuItem, Select, TextField, Typography } from '@mui/material';
import { Barcode, CloseCircle } from 'iconsax-reactjs';
import { BrowserQRCodeReader, type IScannerControls } from '@zxing/browser';
import { t } from '../i18n';

export function QrScanInput({
  label,
  value,
  error,
  helperText,
  onChange
}: {
  label: string;
  value: unknown;
  error?: string;
  helperText?: string;
  onChange: (value: string) => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const controlsRef = useRef<IScannerControls | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [open, setOpen] = useState(false);
  const [scanError, setScanError] = useState<string | null>(null);
  const [cameras, setCameras] = useState<MediaDeviceInfo[]>([]);
  const [selectedCamera, setSelectedCamera] = useState<string | null>(null);

  const stopCamera = () => {
    controlsRef.current?.stop();
    controlsRef.current = null;
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
  };

  useEffect(() => () => stopCamera(), []);

  useEffect(() => {
    if (!open) return undefined;

    let cancelled = false;
    const loadCameras = async () => {
      if (!navigator.mediaDevices?.getUserMedia) {
        setScanError(t('user.cameraUnavailable'));
        return;
      }

      try {
        const permissionStream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
        permissionStream.getTracks().forEach((track) => track.stop());
        const devices = (await navigator.mediaDevices.enumerateDevices()).filter((device) => device.kind === 'videoinput');
        if (cancelled) return;
        setCameras(devices);
        setSelectedCamera((current) => current && devices.some((device) => device.deviceId === current) ? current : devices[0]?.deviceId ?? null);
      } catch {
        setScanError(t('user.cameraPermissionDenied'));
      }
    };

    setScanError(null);
    void loadCameras();
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open || !selectedCamera || !videoRef.current) return undefined;

    let cancelled = false;
    const reader = new BrowserQRCodeReader();
    const video = videoRef.current;
    const start = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { deviceId: { exact: selectedCamera } },
          audio: false
        });
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;
        video.autoplay = true;
        video.muted = true;
        video.playsInline = true;
        video.srcObject = stream;
        await new Promise<void>((resolve) => {
          if (video.readyState >= HTMLMediaElement.HAVE_METADATA) {
            resolve();
            return;
          }
          video.addEventListener('loadedmetadata', () => resolve(), { once: true });
        });
        await video.play();
        await new Promise<void>((resolve) => {
          if (video.readyState >= HTMLMediaElement.HAVE_ENOUGH_DATA) {
            resolve();
            return;
          }
          video.addEventListener('canplay', () => resolve(), { once: true });
        });
        controlsRef.current = await reader.decodeFromStream(stream, video, (result) => {
          const value = result?.getText()?.trim();
          if (!cancelled && value) {
            onChange(value);
            setOpen(false);
          }
        });
      } catch {
        if (!cancelled) setScanError(t('user.qrError'));
      }
    };
    void start();
    return () => {
      cancelled = true;
      stopCamera();
    };
  }, [open, selectedCamera, onChange]);

  const close = () => {
    stopCamera();
    setOpen(false);
  };

  const scanImage = async (file: File) => {
    const url = URL.createObjectURL(file);
    try {
      const result = await new BrowserQRCodeReader().decodeFromImageUrl(url);
      const value = result.getText()?.trim();
      if (value) {
        onChange(value);
        close();
      } else {
        setScanError(t('user.qrImageNotFound'));
      }
    } catch {
      setScanError(t('user.qrImageNotFound'));
    } finally {
      URL.revokeObjectURL(url);
    }
  };

  return (
    <>
      <TextField
        label={label}
        value={(value ?? '') as string}
        onChange={(event) => onChange(event.target.value)}
        fullWidth
        error={Boolean(error)}
        helperText={error ?? helperText}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton aria-label={t('user.scanCard')} title={t('user.scanCard')} onClick={() => { setCameras([]); setSelectedCamera(null); setOpen(true); }} edge="end">
                <Barcode size={20} />
              </IconButton>
            </InputAdornment>
          )
        }}
      />
      <Dialog open={open} onClose={close} fullWidth maxWidth="sm">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {t('user.scanCardTitle')}
          <IconButton aria-label={t('action.close')} onClick={close}>
            <CloseCircle size={20} />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {scanError ? <Alert severity="warning" sx={{ mb: 2 }}>{scanError}</Alert> : null}
          {cameras.length > 1 ? (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>{t('user.camera')}</InputLabel>
              <Select value={selectedCamera ?? ''} label={t('user.camera')} onChange={(event) => setSelectedCamera(event.target.value)}>
                {cameras.map((camera, index) => (
                  <MenuItem key={camera.deviceId} value={camera.deviceId}>
                    {camera.label || `${t('user.camera')} ${index + 1}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : null}
          <Box sx={{ position: 'relative', overflow: 'hidden', borderRadius: 1, bgcolor: 'common.black', aspectRatio: '4 / 3' }}>
            <video
              ref={videoRef}
              muted
              autoPlay
              playsInline
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            {t('user.scanCardHelper')}
          </Typography>
          <input
            ref={imageInputRef}
            type="file"
            accept="image/*"
            hidden
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.target.value = '';
              if (file) void scanImage(file);
            }}
          />
          <Button variant="outlined" onClick={() => imageInputRef.current?.click()} sx={{ mt: 1.5 }}>
            {t('user.scanImage')}
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={close}>{t('action.close')}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
